import { useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { FadeUp, RevealLinesOnView } from "../Reveal";
import { PROCESS, WHY, WORK_CATEGORIES, WORK_ITEMS } from "../../lib/site";

const EASE = [0.16, 1, 0.3, 1];

function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const width = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <section ref={ref} data-testid="process-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-16">
          <p className="overline mb-5 text-pp-red">The Process</p>
          <RevealLinesOnView lines={["FROM PHOTO", "TO FRAME."]} className="font-display text-5xl font-black leading-[0.9] tracking-tight text-pp-ink md:text-7xl" />
        </div>

        <div className="relative">
          {/* Desktop horizontal line */}
          <div className="absolute left-0 right-0 top-8 hidden h-px bg-neutral-200 md:block">
            <motion.div className="h-full bg-pp-red" style={{ width }} />
          </div>
          {/* Mobile vertical line */}
          <div className="absolute bottom-0 left-8 top-0 w-px bg-neutral-200 md:hidden">
            <motion.div className="w-full bg-pp-red" style={{ height }} />
          </div>

          <div className="grid grid-cols-1 gap-10 md:grid-cols-5">
            {PROCESS.map((p, i) => (
              <FadeUp key={p.no} delay={i * 0.08}>
                <div className="relative flex items-start gap-5 md:block">
                  <div className="relative z-10 flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white font-display text-2xl font-black text-pp-red">
                    {p.no}
                  </div>
                  <h3 className="mt-0 font-display text-xl font-bold tracking-tight text-pp-ink md:mt-6 md:text-2xl">{p.title}</h3>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section data-testid="why-section" className="bg-pp-black noise py-24 text-white md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <RevealLinesOnView lines={["WHY PHOTO PIXEL®?"]} className="font-display text-4xl font-black tracking-tight md:text-6xl" />
        <div className="mt-16 grid grid-cols-1 gap-x-10 gap-y-2 md:grid-cols-2">
          {WHY.map((w, i) => (
            <FadeUp key={w.no} delay={(i % 2) * 0.08}>
              <div className="group flex items-center gap-6 border-t border-white/10 py-6 transition-colors hover:border-pp-red">
                <span className="font-display text-3xl font-black text-pp-red md:text-4xl">{w.no}</span>
                <h3 className="font-display text-2xl font-medium tracking-tight md:text-3xl">{w.title}</h3>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Work() {
  const [cat, setCat] = useState("Frames");
  const items = WORK_ITEMS.filter((w) => w.cat === cat);
  return (
    <section data-testid="work-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-10">
          <p className="overline mb-5 text-pp-red">Our Products & Work</p>
          <h2 className="font-display text-4xl font-black tracking-tight text-pp-ink md:text-6xl">A closer look.</h2>
        </div>
        <div className="mb-10 flex flex-wrap gap-2">
          {WORK_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`work-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`pp-btn rounded-full border px-5 py-2.5 text-sm ${
                cat === c ? "border-pp-red bg-pp-red text-white" : "border-neutral-300 text-neutral-600 hover:border-pp-red"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <motion.div layout className="grid grid-cols-2 gap-5 md:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {items.map((it, i) => (
              <motion.div
                key={it.title}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: EASE, delay: i * 0.05 }}
                data-testid={`work-item-${i}`}
                data-cursor="view"
                className="group cursor-pointer"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={it.image} alt={it.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-pp-ink transition-colors group-hover:text-pp-red">{it.title}</h3>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}

export default function ProcessWhy() {
  return (
    <>
      <Process />
      <Why />
      <Work />
    </>
  );
}
